import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import { StarDisplay } from "./Stars";
import NonprofitBadge from "./NonprofitBadge";

const MIN_ITEMS = 3;

function initialsOf(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

function MakerCard({ v }) {
  const display = v.business_name || v.name || "Maker";
  const category = (v.categories && v.categories[0]) || "Independent maker";
  const rating = v.ratings_summary?.overall;
  const count = v.ratings_summary?.count || 0;

  return (
    <Link
      to={`/@${v.slug}`}
      data-testid={`top-rated-card-${v.slug}`}
      className="group block card-warm rounded-sm overflow-hidden transition-transform duration-300 hover:-translate-y-1"
    >
      <div className="aspect-[4/5] overflow-hidden relative bg-[color:var(--sand)]">
        {v.portfolio_url ? (
          <img
            src={v.portfolio_url}
            alt={display}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center font-serif"
            style={{
              background:
                "linear-gradient(135deg, var(--bone), var(--sand, #ece4d6))",
              color: "var(--terracotta)",
              fontSize: "3rem",
              letterSpacing: "0.06em",
            }}
            aria-hidden
          >
            {initialsOf(display) || "M"}
          </div>
        )}
        {rating ? (
          <div
            className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
            style={{
              background: "rgba(255,255,255,0.94)",
              color: "var(--ink)",
              backdropFilter: "blur(6px)",
            }}
            data-testid={`top-rated-badge-${v.slug}`}
          >
            <StarDisplay value={rating} size={12} />
            <span>
              {rating.toFixed(1)}
              <span
                className="ml-1"
                style={{ color: "var(--ink-soft)", fontWeight: 400 }}
              >
                ({count})
              </span>
            </span>
          </div>
        ) : null}
      </div>
      <div className="p-5">
        <div
          className="overline"
          style={{ color: "var(--terracotta)" }}
        >
          {category}
        </div>
        <div className="font-serif text-2xl mt-2 leading-tight">{display}</div>
        <div className="mt-1">
          <NonprofitBadge user={v} size="sm" />
        </div>
        {v.location ? (
          <div
            className="text-xs mt-1"
            style={{ color: "var(--ink-soft)" }}
          >
            {v.location}
          </div>
        ) : null}
        {v.bio ? (
          <p
            className="mt-3 text-sm leading-relaxed line-clamp-2"
            style={{ color: "var(--ink-soft)" }}
          >
            {v.bio}
          </p>
        ) : null}
      </div>
    </Link>
  );
}

export default function TopRatedMakers() {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [nonprofitOnly, setNonprofitOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setItems(null);
        const { data } = await api.get("/vendors/top-rated", {
          params: { limit: 6, nonprofit_only: nonprofitOnly ? "true" : undefined },
        });
        if (!cancelled) setItems(Array.isArray(data?.items) ? data.items : []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nonprofitOnly]);

  // When the toggle is OFF we still hide the section on fresh installs so the
  // landing page doesn't render a 1-2 card orphaned rail. When the toggle is
  // ON we ALWAYS show the section (even with 0 nonprofits) so the toggle
  // itself + the empty-state hint are visible — otherwise users think the
  // toggle broke the page.
  if (!items) return null;
  if (!nonprofitOnly && items.length < MIN_ITEMS) return null;

  return (
    <section
      className="mx-auto max-w-7xl px-6 lg:px-10 pb-24"
      data-testid="top-rated-section"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div
            className="overline"
            style={{ color: "var(--terracotta)" }}
          >
            Trending on AddVendTour
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl mt-3 max-w-2xl">
            Top-rated makers,{" "}
            <span style={{ fontStyle: "italic", color: "var(--terracotta)" }}>
              by their communities.
            </span>
          </h2>
          <p
            className="mt-3 max-w-xl text-sm leading-relaxed"
            style={{ color: "var(--ink-soft)" }}
          >
            Ranked by real organizers who’ve hosted them. Every card links to a
            public portfolio.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label
            className="inline-flex items-center gap-2 cursor-pointer text-xs uppercase tracking-widest"
            data-testid="top-rated-nonprofit-toggle-label"
            style={{ color: nonprofitOnly ? "#4B7A0A" : "var(--ink-soft)" }}
          >
            <input
              type="checkbox"
              checked={nonprofitOnly}
              onChange={(e) => setNonprofitOnly(e.target.checked)}
              data-testid="top-rated-nonprofit-toggle"
              className="h-4 w-4"
              style={{ accentColor: "#84CC16" }}
            />
            ♥ Nonprofits only
          </label>
          <Link
            to="/events"
            data-testid="top-rated-view-all"
            className="btn-outline px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2"
          >
            Find them at an event <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {nonprofitOnly && items.length === 0 && (
        <div
          data-testid="top-rated-nonprofit-empty"
          className="card-warm rounded-xl p-8 text-center"
        >
          <div className="font-serif text-xl">No verified nonprofit vendors yet</div>
          <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
            Uncheck the filter above to see everyone, or visit{" "}
            <Link
              to="/nonprofits"
              className="underline"
              style={{ color: "var(--avt-teal)" }}
            >
              the nonprofit directory
            </Link>
            .
          </p>
        </div>
      )}

      {/* Horizontally-scrollable rail on mobile, grid on md+ */}
      <div
        className="flex md:grid md:grid-cols-3 gap-5 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 pb-2"
        data-testid="top-rated-rail"
      >
        {items.map((v) => (
          <div
            key={v.id}
            className="snap-start shrink-0 w-[78%] sm:w-[52%] md:w-auto"
          >
            <MakerCard v={v} />
          </div>
        ))}
      </div>
    </section>
  );
}
